import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ContactInsight {
  id: string;
  contact_id: string;
  overall_summary?: string;
  goals?: string[];
  pain_points?: string[];
  objection_pattern?: string;
  communication_style?: string;
  trust_level?: number;
  decision_style?: string;
  best_approach_notes?: string;
  risk_flags?: string[];
  disc_profile?: Record<string, any>;
  vak_profile?: Record<string, any>;
  speech_patterns?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export const useContactInsights = (contactId?: string) => {
  const queryClient = useQueryClient();

  const { data: insight, isLoading } = useQuery({
    queryKey: ['contact-insights', contactId],
    queryFn: async () => {
      if (!contactId) return null;
      
      const { data, error } = await supabase
        .from('contact_insights')
        .select('*')
        .eq('contact_id', contactId)
        .maybeSingle();

      if (error) throw error;
      return data as ContactInsight | null;
    },
    enabled: !!contactId,
  });

  const createInsight = useMutation({
    mutationFn: async (insightData: any) => {
      const { data, error } = await supabase
        .from('contact_insights')
        .insert([insightData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-insights'] });
      toast.success('Contact insight created');
    },
    onError: (error: any) => {
      toast.error(`Failed to create insight: ${error.message}`);
    },
  });

  const updateInsight = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ContactInsight> }) => {
      const { data, error } = await supabase
        .from('contact_insights')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-insights'] });
      toast.success('Contact insight updated');
    },
    onError: (error: any) => {
      toast.error(`Failed to update insight: ${error.message}`);
    },
  });

  return {
    insight,
    isLoading,
    createInsight: createInsight.mutate,
    updateInsight: updateInsight.mutate,
  };
};
